import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  addDays,
  clientBalances,
  clientName,
  formatPrice,
  monthRange,
  monthlyTotals,
  perClient,
  periodSummary,
  weekRange,
  type Client,
  type ClientBalance,
} from '@diana/shared'
import { api } from '../../lib/api.js'
import { MONTH_NAMES, weekTitle } from '../../lib/datetime.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { ErrorLine, Skeleton, Stat, StatGrid, Tabs } from '../../classroom/ui.js'
import { Balance } from './ClientsPage.js'
import { Standing, lastPackage, lessonsWord, packageLeft, plural, td, th } from './books.js'
import { downloadCsv } from '../../lib/csv.js'

/**
 * Studio → Rozliczenia (Studio-Accounting.png). One request, then everything
 * is sliced in the browser with @diana/shared/accounting — month, week, year,
 * per client, per tag.
 *
 * The top bar picks the month: three numbers for it, then every student who
 * had lessons, paid, or owes. Below, the week and the year as before, and the
 * all-time balances with their exact amounts.
 *
 * "Earned" means lessons that happened (or are happening today); a lesson
 * next Tuesday is "planned". Pending requests and cancellations are not
 * money. The rules live in one place: packages/shared/src/accounting.ts.
 */
export function AccountingPage() {
  const { data, isPending, isError } = useQuery({ queryKey: ['studio', 'accounting'], queryFn: api.studio.accounting })

  const [tag, setTag] = useState('')
  const [weekAnchor, setWeekAnchor] = useState<string | null>(null)
  const [monthAnchor, setMonthAnchor] = useState<string | null>(null)
  const [year, setYear] = useState<number | null>(null)

  const today = data?.today ?? ''
  const wk = weekAnchor ?? today
  const mo = monthAnchor ?? today
  const yr = year ?? Number(today.slice(0, 4))

  const clients = useMemo(() => data?.clients ?? [], [data])
  const purchases = useMemo(() => data?.purchases ?? [], [data])
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])
  const allTags = useMemo(() => [...new Set(clients.flatMap((c) => c.tags))].sort(), [clients])

  /** The tag filter applies to everything on the page: a tagged client's lessons and purchases. */
  const scoped = useMemo(() => {
    if (!data) return { clients: [] as Client[], lessons: [], purchases: [] }
    const keep = new Set(clients.filter((c) => !tag || c.tags.includes(tag)).map((c) => c.id))
    return {
      clients: clients.filter((c) => keep.has(c.id)),
      lessons: data.lessons.filter((l) => l.clientId && keep.has(l.clientId)),
      purchases: data.purchases.filter((p) => keep.has(p.clientId)),
    }
  }, [data, clients, tag])

  const balances = useMemo(() => {
    if (!today) return [] as ClientBalance[]
    return clientBalances(scoped.clients, scoped.lessons, scoped.purchases, today)
  }, [scoped, today])
  const balanceOf = useMemo(() => new Map(balances.map((b) => [b.clientId, b])), [balances])

  const month = useMemo(() => {
    if (!today) return null
    const [y, m] = mo.split('-').map(Number) as [number, number]
    const r = monthRange(y, m - 1)
    const lessons = new Map(perClient(scoped.lessons, r.from, r.to, today).map((row) => [row.clientId, row]))
    const paid = new Map<string, number>()
    for (const p of scoped.purchases) {
      const day = String(p.purchasedAt).slice(0, 10)
      if (day >= r.from && day <= r.to) paid.set(p.clientId, (paid.get(p.clientId) ?? 0) + p.totalGrosz)
    }
    /* Everyone with lessons this month, then anyone who paid or still owes. */
    const ids = [...lessons.keys()]
    for (const id of paid.keys()) if (!ids.includes(id)) ids.push(id)
    for (const b of balances) if (b.balanceGrosz < 0 && !ids.includes(b.clientId)) ids.push(b.clientId)
    const rows = ids.map((id) => ({
      clientId: id,
      lessonsDone: lessons.get(id)?.lessonsDone ?? 0,
      lessonsPlanned: lessons.get(id)?.lessonsPlanned ?? 0,
      earnedGrosz: lessons.get(id)?.earnedGrosz ?? 0,
      plannedGrosz: lessons.get(id)?.plannedGrosz ?? 0,
      paidGrosz: paid.get(id) ?? 0,
    }))
    return {
      y,
      m,
      ...r,
      rows,
      total: periodSummary(scoped.lessons, r.from, r.to, today),
      paidTotal: [...paid.values()].reduce((s, g) => s + g, 0),
    }
  }, [scoped, balances, mo, today])

  const week = useMemo(() => {
    if (!today) return null
    const r = weekRange(wk)
    return { ...r, rows: perClient(scoped.lessons, r.from, r.to, today), total: periodSummary(scoped.lessons, r.from, r.to, today) }
  }, [scoped, wk, today])

  const months = useMemo(() => (today ? monthlyTotals(scoped.lessons, yr, today) : []), [scoped, yr, today])

  const nameOf = (id: string) => {
    const c = clientById.get(id)
    return c ? clientName(c) : 'Usunięty uczeń'
  }
  const pkgOf = (id: string) => lastPackage(id, purchases)

  const thisMonth = today ? `${today.slice(0, 7)}-01` : ''
  const monthLabel = month ? `${MONTH_NAMES[month.m - 1]!.toLocaleLowerCase('pl')} ${month.y}` : ''
  const shiftMonth = (delta: number) => {
    if (!month) return
    const m = month.m - 1 + delta
    const y = month.y + Math.floor(m / 12)
    setMonthAnchor(monthRange(y, ((m % 12) + 12) % 12).from)
  }

  const exportCsv = () => {
    if (!month) return
    const rows: (string | number)[][] = [
      ['Uczeń', 'Rozliczenie', 'Tagi', 'Lekcje odbyte', 'Lekcje zaplanowane', 'Wartość (zł)', 'Zaplanowane (zł)', 'Wpłacone (zł)', 'W pakiecie', 'Saldo (zł)'],
    ]
    for (const r of month.rows) {
      const pkg = pkgOf(r.clientId)
      const b = balanceOf.get(r.clientId)
      rows.push([
        nameOf(r.clientId),
        pkg ? `pakiet ${pkg.lessons} ${lessonsWord(pkg.lessons)}` : 'po lekcji',
        (clientById.get(r.clientId)?.tags ?? []).join(', '),
        r.lessonsDone,
        r.lessonsPlanned,
        zl(r.earnedGrosz),
        zl(r.plannedGrosz),
        zl(r.paidGrosz),
        packageLeft(b, pkg, false),
        zl(b?.balanceGrosz ?? 0),
      ])
    }
    rows.push(['Razem', '', '', month.total.lessonsDone, month.total.lessonsPlanned, zl(month.total.earnedGrosz), zl(month.total.plannedGrosz), zl(month.paidTotal), '', ''])
    downloadCsv(`rozliczenia-${month.from.slice(0, 7)}${tag ? `-${tag.replace(/\s+/g, '-')}` : ''}.csv`, rows)
  }

  const owing = balances.filter((b) => b.balanceGrosz < 0)
  const owed = owing.reduce((s, b) => s - b.balanceGrosz, 0)
  const credit = balances.filter((b) => b.balanceGrosz > 0).reduce((s, b) => s + b.balanceGrosz, 0)

  return (
    <Column
      density="studio"
      topOnPhone
      top={
        <>
          <TopTitle studio>Rozliczenia</TopTitle>
          {month && (
            <>
              <Stepper
                className="ml-1 desk:ml-5"
                label={monthLabel}
                prev="Poprzedni miesiąc"
                next="Następny miesiąc"
                onPrev={() => shiftMonth(-1)}
                onNext={() => shiftMonth(1)}
                hover="hover:bg-class-white/70"
              />
              {month.from !== thisMonth && (
                <button type="button" onClick={() => setMonthAnchor(null)} className="cl-link-quiet hidden text-class-gloss desk:inline">
                  wróć do bieżącego
                </button>
              )}
              <button type="button" onClick={exportCsv} className="cl-link ml-auto hidden text-class-gloss desk:inline">
                Eksport CSV
              </button>
            </>
          )}
        </>
      }
    >
      <Main density="studio">
        {isPending ? (
          <Skeleton lines={5} />
        ) : isError || !data || !month || !week ? (
          <ErrorLine>Nie udało się wczytać rozliczeń. Odśwież stronę.</ErrorLine>
        ) : (
          <>
            <StatGrid>
              <Stat
                label="Zarobione"
                value={formatPrice(month.total.earnedGrosz)}
                hint={`${month.total.lessonsDone} ${plural(month.total.lessonsDone, 'odbyta lekcja', 'odbyte lekcje', 'odbytych lekcji')}`}
              />
              <Stat
                label="Zaplanowane"
                value={formatPrice(month.total.plannedGrosz)}
                hint={
                  month.to < today
                    ? 'miesiąc minął'
                    : `${month.total.lessonsPlanned} ${lessonsWord(month.total.lessonsPlanned)} do końca miesiąca`
                }
              />
              <Stat
                label="Do zapłaty"
                value={formatPrice(owed)}
                hint={[
                  `${owing.length} ${plural(owing.length, 'osoba', 'osoby', 'osób')}`,
                  credit > 0 ? `przedpłacone ${formatPrice(credit)}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </StatGrid>

            {/* ── the month, by student ─────────────────────────────────── */}
            <section className="flex flex-col gap-5">
              <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-class-line">
                <h2 className="cl-subheading m-0 mr-3 pb-2.5">Uczniowie</h2>
                {allTags.length > 0 && (
                  <Tabs
                    label="Tag"
                    value={tag}
                    onChange={setTag}
                    items={[{ value: '', label: 'Wszyscy' }, ...allTags.map((t) => ({ value: t, label: t }))]}
                  />
                )}
              </div>

              {month.rows.length === 0 ? (
                <p className="m-0 py-2 text-class-ink-soft">W tym miesiącu nie było lekcji ani wpłat.</p>
              ) : (
                <>
                  <Table min={720}>
                    <thead>
                      <tr>
                        <th className={th}>Uczeń</th>
                        <th className={`${th} text-right`}>Lekcje</th>
                        <th className={`${th} text-right`}>Wartość</th>
                        <th className={`${th} text-right`}>Wpłacone</th>
                        <th className={`${th} text-right`}>W pakiecie</th>
                        <th className={`${th} text-right`}>Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {month.rows.map((r) => {
                        const pkg = pkgOf(r.clientId)
                        const b = balanceOf.get(r.clientId)
                        return (
                          <tr key={r.clientId}>
                            <td className={td}>
                              <Link to={`/studio/klienci/${r.clientId}`} className="block leading-snug">
                                <span className="block font-semibold">{nameOf(r.clientId)}</span>
                                <span className="block text-class-label text-class-ink-faint">
                                  {pkg ? `pakiet ${pkg.lessons} ${lessonsWord(pkg.lessons)}` : 'po lekcji'}
                                </span>
                              </Link>
                            </td>
                            <td className={`${td} text-right`}>
                              {r.lessonsDone}
                              <Planned>{r.lessonsPlanned > 0 && `+${r.lessonsPlanned}`}</Planned>
                            </td>
                            <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>
                              {formatPrice(r.earnedGrosz)}
                              <Planned>{r.plannedGrosz > 0 && `+${formatPrice(r.plannedGrosz)}`}</Planned>
                            </td>
                            <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>
                              {r.paidGrosz ? formatPrice(r.paidGrosz) : '—'}
                            </td>
                            <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>{packageLeft(b, pkg, false)}</td>
                            <td className={`${td} text-right`}>
                              <Standing b={b} pkg={pkg} usedUp="nowy pakiet?" />
                            </td>
                          </tr>
                        )
                      })}
                      <tr className="font-bold">
                        <td className={td}>Razem</td>
                        <td className={`${td} text-right`}>
                          {month.total.lessonsDone}
                          <Planned>{month.total.lessonsPlanned > 0 && `+${month.total.lessonsPlanned}`}</Planned>
                        </td>
                        <td className={`${td} whitespace-nowrap text-right`}>
                          {formatPrice(month.total.earnedGrosz)}
                          <Planned>{month.total.plannedGrosz > 0 && `+${formatPrice(month.total.plannedGrosz)}`}</Planned>
                        </td>
                        <td className={`${td} whitespace-nowrap text-right`}>{formatPrice(month.paidTotal)}</td>
                        <td className={td} />
                        <td className={td} />
                      </tr>
                    </tbody>
                  </Table>
                  <p className="m-0 text-class-gloss text-class-ink-faint">
                    Szare „+” to lekcje zaplanowane do końca miesiąca. „W pakiecie” i „Saldo” — stan na dziś.
                  </p>
                </>
              )}
            </section>

            {/* ── a week, by student ────────────────────────────────────── */}
            <section className="flex flex-col">
              <Head
                title="Tydzień"
                nav={
                  <Stepper
                    label={weekTitle([week.from, week.to])}
                    prev="Poprzedni tydzień"
                    next="Następny tydzień"
                    onPrev={() => setWeekAnchor(addDays(wk, -7))}
                    onNext={() => setWeekAnchor(addDays(wk, 7))}
                    onToday={weekRange(today).from !== week.from ? () => setWeekAnchor(null) : undefined}
                  />
                }
              />
              {week.rows.length === 0 ? (
                <p className="m-0 border-t border-class-line pt-4 text-class-ink-soft">Żadnych lekcji w tym tygodniu.</p>
              ) : (
                <Table min={480}>
                  <thead>
                    <tr>
                      <th className={th}>Uczeń</th>
                      <th className={`${th} text-right`}>Lekcje</th>
                      <th className={`${th} text-right`}>Zarobione</th>
                      <th className={`${th} text-right`}>Zaplanowane</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week.rows.map((r) => (
                      <tr key={r.clientId}>
                        <td className={td}>
                          <Link to={`/studio/klienci/${r.clientId}`} className="font-semibold">
                            {nameOf(r.clientId)}
                          </Link>
                        </td>
                        <td className={`${td} text-right`}>
                          {r.lessonsDone}
                          <Planned>{r.lessonsPlanned > 0 && `+${r.lessonsPlanned}`}</Planned>
                        </td>
                        <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>{formatPrice(r.earnedGrosz)}</td>
                        <td className={`${td} whitespace-nowrap text-right text-class-ink-faint`}>
                          {r.plannedGrosz ? formatPrice(r.plannedGrosz) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-bold">
                      <td className={td}>Razem</td>
                      <td className={`${td} text-right`}>
                        {week.total.lessonsDone}
                        <Planned>{week.total.lessonsPlanned > 0 && `+${week.total.lessonsPlanned}`}</Planned>
                      </td>
                      <td className={`${td} whitespace-nowrap text-right`}>{formatPrice(week.total.earnedGrosz)}</td>
                      <td className={`${td} whitespace-nowrap text-right`}>
                        {week.total.plannedGrosz ? formatPrice(week.total.plannedGrosz) : '—'}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </section>

            {/* ── a year, by month ──────────────────────────────────────── */}
            <section className="flex flex-col">
              <Head
                title="Rok"
                nav={
                  <Stepper
                    label={String(yr)}
                    prev="Poprzedni rok"
                    next="Następny rok"
                    onPrev={() => setYear(yr - 1)}
                    onNext={() => setYear(yr + 1)}
                    onToday={year !== null && year !== Number(today.slice(0, 4)) ? () => setYear(null) : undefined}
                  />
                }
              />
              <Table min={480}>
                <thead>
                  <tr>
                    <th className={th}>Miesiąc</th>
                    <th className={`${th} text-right`}>Lekcje</th>
                    <th className={`${th} text-right`}>Zarobione</th>
                    <th className={`${th} text-right`}>Zaplanowane</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, i) => {
                    const empty = m.earnedGrosz === 0 && m.plannedGrosz === 0 && m.lessonsDone === 0
                    return (
                      <tr key={i} className={empty ? 'text-class-ink-faint' : ''}>
                        <td className={`${tdShort}`}>{MONTH_NAMES[i]}</td>
                        <td className={`${tdShort} text-right`}>{m.lessonsDone || ''}</td>
                        <td className={`${tdShort} whitespace-nowrap text-right`}>{m.earnedGrosz ? formatPrice(m.earnedGrosz) : ''}</td>
                        <td className={`${tdShort} whitespace-nowrap text-right text-class-ink-faint`}>
                          {m.plannedGrosz ? formatPrice(m.plannedGrosz) : ''}
                        </td>
                      </tr>
                    )
                  })}
                  <tr className="font-bold">
                    <td className={td}>Razem {yr}</td>
                    <td className={`${td} text-right`}>{months.reduce((s, m) => s + m.lessonsDone, 0)}</td>
                    <td className={`${td} whitespace-nowrap text-right`}>
                      {formatPrice(months.reduce((s, m) => s + m.earnedGrosz, 0))}
                    </td>
                    <td className={`${td} whitespace-nowrap text-right`}>
                      {formatPrice(months.reduce((s, m) => s + m.plannedGrosz, 0))}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </section>

            {/* ── all-time balances, with the exact amounts ─────────────── */}
            <section className="flex flex-col">
              <Head title="Salda od początku" />
              {balances.length === 0 ? (
                <p className="m-0 border-t border-class-line pt-4 text-class-ink-soft">Brak uczniów w tym filtrze.</p>
              ) : (
                <Table min={640}>
                  <thead>
                    <tr>
                      <th className={th}>Uczeń</th>
                      <th className={`${th} text-right`}>Lekcje</th>
                      <th className={`${th} text-right`}>Wartość</th>
                      <th className={`${th} text-right`}>Wpłacone</th>
                      <th className={`${th} text-right`}>W pakiecie</th>
                      <th className={`${th} text-right`}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...balances]
                      .sort((a, b) => a.balanceGrosz - b.balanceGrosz)
                      .map((b) => (
                        <tr key={b.clientId}>
                          <td className={td}>
                            <Link to={`/studio/klienci/${b.clientId}`} className="font-semibold">
                              {nameOf(b.clientId)}
                            </Link>
                          </td>
                          <td className={`${td} text-right`}>{b.lessonsDone}</td>
                          <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>{formatPrice(b.earnedGrosz)}</td>
                          <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>{formatPrice(b.paidGrosz)}</td>
                          <td className={`${td} whitespace-nowrap text-right text-class-ink-soft`}>
                            {b.lessonsBought > 0 ? Math.max(0, b.lessonsRemaining) : '—'}
                          </td>
                          <td className={`${td} whitespace-nowrap text-right font-semibold`}>
                            <Balance grosz={b.balanceGrosz} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              )}
            </section>
          </>
        )}
      </Main>
    </Column>
  )
}

/** A shorter row for the twelve months. */
const tdShort = td.replace('h-[60px]', 'h-12')

/** The grey „+2” after a number: what is planned but has not happened yet. */
function Planned({ children }: { children: ReactNode }) {
  if (!children) return null
  return <span className="ml-1.5 font-normal text-class-ink-faint">{children}</span>
}

function Table({ min, children }: { min: number; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table style={{ minWidth: min }} className="w-full border-collapse text-left text-class-gloss">
        {children}
      </table>
    </div>
  )
}

/** An 18px section head with an optional stepper on the right. */
function Head({ title, nav }: { title: string; nav?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-3">
      <h2 className="cl-subheading m-0">{title}</h2>
      {nav && <div className="ml-auto">{nav}</div>}
    </div>
  )
}

/** ‹ label › — and „dziś” when the view has moved away from today. */
function Stepper({
  label,
  prev,
  next,
  onPrev,
  onNext,
  onToday,
  className = '',
  hover = 'hover:bg-class-field-soft',
}: {
  label: string
  prev: string
  next: string
  onPrev: () => void
  onNext: () => void
  onToday?: () => void
  className?: string
  hover?: string
}) {
  const arrow = `inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-class-sm border-0 bg-transparent p-0 font-class-text text-class-body text-class-ink-soft ${hover} hover:text-class-ink`
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <button type="button" aria-label={prev} onClick={onPrev} className={arrow}>
        ‹
      </button>
      <span className="min-w-[8.5rem] text-center font-semibold whitespace-nowrap">{label}</span>
      <button type="button" aria-label={next} onClick={onNext} className={arrow}>
        ›
      </button>
      {onToday && (
        <button type="button" onClick={onToday} className="cl-link-quiet ml-2 text-class-gloss">
          dziś
        </button>
      )}
    </div>
  )
}

/** Grosze as „480,00” — the decimal comma a Polish spreadsheet expects. */
const zl = (grosz: number) => (grosz / 100).toFixed(2).replace('.', ',')

